import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { 
  setUpTOTP, 
  verifyTOTPSetup, 
  getCurrentUser,
  fetchMFAPreference
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';

const MFASetup = () => {
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify, 3: Backup codes
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for authentication state to be loaded
    if (authLoading) {
      setIsCheckingAuth(true);
      return;
    }
    
    setIsCheckingAuth(false);
    
    // Only redirect if not authenticated after loading is complete
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if user already has MFA enabled
    checkMFAStatus();
  }, [isAuthenticated, authLoading, navigate]);

  const checkMFAStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log('Current user MFA status:', {
        preferredMfaSetting: currentUser.preferredMfaSetting,
        enabledMfaTypes: currentUser.enabledMfaTypes,
        user: currentUser
      });
      
      // Also fetch MFA preference to get more details
      try {
        const mfaPreference = await fetchMFAPreference();
        console.log('MFA Preference:', mfaPreference);
      } catch (mfaError) {
        console.log('Could not fetch MFA preference:', mfaError);
      }
      
      // If user already has MFA enabled, redirect to dashboard
      if (currentUser.preferredMfaSetting === 'SOFTWARE_TOKEN_MFA') {
        console.log('User already has MFA enabled, redirecting to dashboard');
        navigate('/app/dashboard'); // '/dashboard' fell through to the marketing homepage
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
      // Don't redirect on error, just log it
    }
  };

  const handleSetupMFA = async () => {
    console.log('MFA Setup button clicked');
    setIsLoading(true);
    setErrors({});

    try {
      console.log('Calling setUpTOTP...');
      console.log('setUpTOTP function:', setUpTOTP);
      console.log('typeof setUpTOTP:', typeof setUpTOTP);
      
      const currentUser = await getCurrentUser();
      console.log('Current user before setUpTOTP:', currentUser);
      console.log('User session:', currentUser.signInDetails);
      
      if (typeof setUpTOTP !== 'function') {
        throw new Error('setUpTOTP is not a function');
      }
      
      // Try calling setUpTOTP with different parameters
      console.log('Attempting setUpTOTP call...');
      const result = await setUpTOTP();
      console.log('setUpTOTP result:', result);
      console.log('Result type:', typeof result);
      console.log('Result keys:', result ? Object.keys(result) : 'No result');
      
      if (result.sharedSecret) {
        setSecretKey(result.sharedSecret);
        
        // Use the getSetupUri function to get the proper QR code URI
        const qrCodeUri = result.getSetupUri('Finnewsy', currentUser.username);
        setQrCode(qrCodeUri);
        setStep(2);
        
        toast({
          title: 'MFA Setup Started',
          description: 'Please scan the QR code with your authenticator app and enter the verification code.',
          variant: 'success'
        });
      } else {
        throw new Error('No shared secret returned from setUpTOTP');
      }
    } catch (error) {
      console.error('Error setting up MFA:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.error('Full error object:', error);
      
      // Show more specific error message
      const errorMessage = error.message || 'Failed to setup MFA. Please try again.';
      setErrors({ general: errorMessage });
      
      toast({
        title: 'Setup Failed',
        description: errorMessage,
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMFA = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Validate verification code
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verificationCode: 'Please enter a valid 6-digit code' });
      setIsLoading(false);
      return;
    }

    try {
      await verifyTOTPSetup({
        code: verificationCode
      });

      // Generate backup codes (simplified - in real app, this would be server-side)
      const codes = generateBackupCodes();
      setBackupCodes(codes);
      setStep(3);

      toast({
        title: 'MFA Enabled Successfully',
        description: 'Multi-factor authentication has been enabled for your account.',
        variant: 'success'
      });
    } catch (error) {
      console.error('Error verifying MFA:', error);
      setErrors({ verificationCode: 'Invalid verification code. Please try again.' });
      
      toast({
        title: 'Verification Failed',
        description: 'Invalid verification code. Please check your authenticator app.',
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateBackupCodes = () => {
    // Generate 10 backup codes (simplified implementation)
    const codes = [];
    for (let i = 0; i < 10; i++) {
      codes.push(Math.random().toString(36).substring(2, 10).toUpperCase());
    }
    return codes;
  };

  const handleCompleteSetup = () => {
    toast({
      title: 'Setup Complete',
      description: 'MFA has been successfully enabled. Please save your backup codes securely.',
      variant: 'success'
    });
    navigate('/app/dashboard');
  };

  const handleSkipMFA = () => {
    toast({
      title: 'MFA Skipped',
      description: 'You can enable MFA later in your account settings.',
      variant: 'info'
    });
    navigate('/app/dashboard');
  };

  if (isCheckingAuth || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Enable Multi-Factor Authentication
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Secure your account with an additional layer of protection
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    What is Multi-Factor Authentication?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      MFA adds an extra layer of security by requiring a second form of verification 
                      in addition to your password. This helps protect your account even if your password is compromised.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">How it works:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                <li>Scan the QR code with your authenticator app</li>
                <li>Enter the 6-digit code from your app to verify setup</li>
                <li>Save your backup codes in a secure location</li>
              </ol>
            </div>

            <div className="flex space-x-4">
              <Button
                onClick={handleSetupMFA}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? 'Setting up...' : 'Enable MFA'}
              </Button>
              <Button
                onClick={handleSkipMFA}
                variant="outline"
                className="flex-1"
              >
                Skip for Now
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyMFA} className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Step 2: Verify Setup</h3>
              
              <div className="space-y-4">
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scan this QR code with your authenticator app:
                  </label>
                  <div className="bg-white p-4 rounded-lg border border-gray-300 inline-block">
                    {qrCode && (
                      <QRCodeSVG 
                        value={qrCode} 
                        size={200} 
                        level="M"
                        includeMargin={true}
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Secret Key (if QR code doesn&apos;t work):
                  </label>
                  <div className="mt-1 p-3 bg-white border border-gray-300 rounded-md font-mono text-sm">
                    {secretKey}
                  </div>
                </div>

                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                    Enter 6-digit code from your authenticator app:
                  </label>
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    maxLength="6"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="123456"
                  />
                  {errors.verificationCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.verificationCode}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <Button
                type="submit"
                disabled={isLoading || !verificationCode}
                className="flex-1"
              >
                {isLoading ? 'Verifying...' : 'Verify & Enable MFA'}
              </Button>
              <Button
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    MFA Successfully Enabled!
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      Your account is now protected with multi-factor authentication.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Backup Codes</h3>
              <p className="text-sm text-gray-600 mb-4">
                Save these backup codes in a secure location. You can use them to access your account 
                if you lose access to your authenticator app.
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="p-2 bg-white border border-gray-300 rounded text-center">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="mt-2 text-xs text-red-600">
                ⚠️ Each backup code can only be used once. Store them securely!
              </p>
            </div>

            <Button
              onClick={handleCompleteSetup}
              className="w-full"
            >
              Complete Setup
            </Button>
          </div>
        )}

        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
        )}

        <div className="text-center">
          <Link
            to="/app/dashboard"
            className="text-sm text-primary hover:text-primary-dark"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
